import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import "./Home.css";

import {
  FiShield,
  FiTruck,
  FiRefreshCcw,
  FiCheckCircle,
  FiLock,
  FiGift,
  FiHeart,
  FiStar,
  FiMessageCircle,
} from "react-icons/fi";

// ✅ Existing assets you already have
import traditionalImg from "../assets/traditional.png";
import westernImg from "../assets/western.png";
import bagsImg from "../assets/bag1.jpg";

// ✅ HERO video
import heroVideo from "../assets/hero-video.mp4";

// Featured images
import featuredJewellery from "../assets/featured-jewel.png";
import featuredWestern from "../assets/featured-western.png";
import featuredTraditional from "../assets/featured-trad.png";
import featuredBags from "../assets/featured-bag.webp";

import pendent from "../assets/pendant1.jpeg";
import earrings from "../assets/monalisa1.jpeg";
import bracelet from "../assets/cuff-bracelet1.jpeg";
import bangle from "../assets/unique-bangle1.jpeg";
import sets from "../assets/haram1.webp";

export default function Home() {
  // ✅ HERO CAROUSEL
  const slides = useMemo(
    () => [
      {
  key: "s1",
  type: "video",
  tag: "New • 2026 Drop",
  title: "New Arrivals, Timeless Style",
  subtitle:
    "Discover curated pieces designed to elevate your everyday look — elegant, effortless, and modern.",
  primaryText: "Shop Jewellery",
  primaryLink: "/jewellery",
  video: heroVideo,
},

      // ✅ UPDATED SLIDE 2 WITH TEXT LIKE OTHER SLIDES
      {
        key: "s2",
        type: "splitFull",
        tag: "Elegance • Redefined",
        title: "Where Tradition Meets Trend",
        subtitle:
          "Celebrate heritage silhouettes and contemporary styles curated for every occasion — festive, formal, or everyday chic.",
        primaryText: "Shop Traditional Wear",
        primaryLink: "/traditional-western?mode=traditional",
        secondaryText: "Explore Western Wear",
        secondaryLink: "/traditional-western?mode=western",
        leftImage: traditionalImg,
        rightImage: westernImg,
      },

      {
        key: "s3",
        type: "image",
        tag: "Premium • Essentials",
        title: "Premium Bags & Purses",
        subtitle:
          "Crafted details, refined silhouettes, and premium finishes — carry luxury everywhere you go.",
        primaryText: "Shop Bags",
        primaryLink: "/bags-purses",
        secondaryText: "Contact Us",
        secondaryLink: "/contact",
        image: bagsImg,
        imageAlt: "Bags & Purses",
      },
    ],
    []
  );

  const [heroIndex, setHeroIndex] = useState(0);
  const [isAnimating, setIsAnimating] = useState(false);

  const goTo = (i) => {
    if (i === heroIndex || isAnimating) return;
    setIsAnimating(true);
    setHeroIndex(i);
    window.scrollTo({ top: 0, behavior: "smooth" });
    window.setTimeout(() => setIsAnimating(false), 900);
  };

  const prev = () => goTo((heroIndex - 1 + slides.length) % slides.length);
  const next = () => goTo((heroIndex + 1) % slides.length);

  useEffect(() => {
    if (isAnimating) return;

    const t = setInterval(() => {
      setIsAnimating(true);
      setHeroIndex((i) => (i + 1) % slides.length);
      window.scrollTo({ top: 0, behavior: "smooth" });
      window.setTimeout(() => setIsAnimating(false), 900);
    }, 6000);

    return () => clearInterval(t);
  }, [slides.length, isAnimating]);

  // ✅ Category cards
  const categories = useMemo(
    () => [
      { title: "Pendants", to: "/jewellery?cat=necklace", img: pendent },
      { title: "Earrings", to: "/jewellery?cat=earrings", img: earrings },
      { title: "Sets", to: "/jewellery?cat=necklace", img: sets },
      { title: "Bangles", to: "/jewellery?cat=bangles", img: bangle },
      { title: "Bracelets", to: "/jewellery?cat=bracelets", img: bracelet },
    ],
    []
  );

  // ✅ Featured section
  const featured = useMemo(
    () => [
      { title: "Artificial Jewellery", to: "/jewellery", img: featuredJewellery },
      {
        title: "Western Wear",
        to: "/traditional-western?mode=western",
        img: featuredWestern,
      },
      {
        title: "Traditional Wear",
        to: "/traditional-western?mode=traditional",
        img: featuredTraditional,
      },
      { title: "Bags & Purses", to: "/bags-purses", img: featuredBags },
    ],
    []
  );

  // ✅ Promises
  const promises = useMemo(
    () => [
      { icon: <FiTruck />, title: "Fast Shipping", sub: "Carefully packed & shipped quickly." },
      { icon: <FiShield />, title: "Trusted Quality", sub: "Curated pieces, premium finish." },
      { icon: <FiLock />, title: "Secure Payments", sub: "Safe checkout you can rely on." },
      { icon: <FiRefreshCcw />, title: "Easy Returns", sub: "Simple support if you need help." },
      { icon: <FiCheckCircle />, title: "Verified Styles", sub: "Wear-tested & trend-ready." },
      { icon: <FiGift />, title: "Gift-Ready", sub: "Perfect for occasions & surprises." },
      { icon: <FiHeart />, title: "Made for Her", sub: "Jewellery, wear & bags in one place." },
      { icon: <FiStar />, title: "Premium Look", sub: "Luxury feel at everyday ease." },
      { icon: <FiMessageCircle />, title: "Support", sub: "We’re here to help you choose." },
    ],
    []
  );

  // ✅ Price filter cards
  const priceFilters = useMemo(
    () => [
      { label: "Under", price: "₹99", to: "/under99" },
      { label: "Under", price: "₹499", to: "/under499" },
      { label: "Under", price: "₹999", to: "/under999" },
      { label: "Under", price: "₹1499", to: "/under1499" },
    ],
    []
  );

  // ✅ Testimonials (unique)
  const testimonials = useMemo(
    () => [
      {
        name: "Ananya",
        place: "Hyderabad",
        rating: 5,
        text:
          "The finish looks premium and the earrings were exactly like the photos. Super happy with the packing too!",
        tag: "Jewellery",
      },
      {
        name: "Meera",
        place: "Bengaluru",
        rating: 5,
        text:
          "Fast delivery + amazing quality for the price. My kurti styling finally feels complete!",
        tag: "Traditional Wear",
      },
      {
        name: "Sara",
        place: "Chennai",
        rating: 4,
        text:
          "The bag looks classy and the stitching is neat. Loved the ‘gift-ready’ vibe — feels luxury.",
        tag: "Bags",
      },
    ],
    []
  );

  const avgRating = useMemo(() => {
    const total = testimonials.reduce((s, t) => s + t.rating, 0);
    return (total / testimonials.length).toFixed(1);
  }, [testimonials]);

  const renderStars = (n) =>
    Array.from({ length: 5 }).map((_, i) => (
      <span key={i} className={`tStar ${i < n ? "on" : ""}`} aria-hidden="true">
        ★
      </span>
    ));

  return (
    <div className="home">
      {/* ✅ HERO CAROUSEL */}
      <section className="hero">
        <div className="heroFrame">
          {slides.map((s, i) => {
            const active = i === heroIndex;

            return (
              <div
                key={s.key}
                className={`heroSlide ${active ? "active" : ""} ${
                  s.type === "splitFull" ? "isSplit" : ""
                }`}
                aria-hidden={!active}
              >
                {s.type === "video" && (
                  <div className="heroBg">
                    <video
                      className="heroBgVideo"
                      src={s.video}
                      autoPlay
                      loop
                      muted
                      playsInline
                    />
                  </div>
                )}

                {s.type === "image" && (
                  <div className="heroBg">
                    <img className="heroBgImg" src={s.image} alt={s.imageAlt} />
                  </div>
                )}

                {s.type === "splitFull" && (
                  <div className="heroSplitBg" aria-hidden="true">
                    <div className="heroHalf" style={{ backgroundImage: `url(${s.leftImage})` }} />
                    <div className="heroHalf" style={{ backgroundImage: `url(${s.rightImage})` }} />
                  </div>
                )}

                <div className={`heroInner ${s.type === "splitFull" ? "centerOnly" : ""}`}>
                  {s.type === "splitFull" ? (
                    <div className="heroContent heroCenterContent">
                      <p className="heroTag">{s.tag}</p>
                      <h1 className="heroTitle">{s.title}</h1>
                      <p className="heroSub">{s.subtitle}</p>

                      <div className="heroBtns">
                        <Link className="heroBtnPrimary" to={s.primaryLink}>
                          {s.primaryText}
                        </Link>
                        {s.secondaryText && s.secondaryLink ? (
  <Link className="heroBtnGhost" to={s.secondaryLink}>
    {s.secondaryText}
  </Link>
) : null}
                      </div>
                    </div>
                  ) : (
                    <div className="heroContent">
                      <p className="heroTag">{s.tag}</p>
                      <h1 className="heroTitle">{s.title}</h1>
                      <p className="heroSub">{s.subtitle}</p>

                      <div className="heroBtns">
                        <Link className="heroBtnPrimary" to={s.primaryLink}>
                          {s.primaryText}
                        </Link>
                        <Link className="heroBtnGhost" to={s.secondaryLink}>
                          {s.secondaryText}
                        </Link>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            );
          })}

          <button className="heroArrow left" onClick={prev} aria-label="Previous slide">
            ‹
          </button>
          <button className="heroArrow right" onClick={next} aria-label="Next slide">
            ›
          </button>

          <div className="heroDots" role="tablist" aria-label="Hero carousel pagination">
            {slides.map((s, i) => (
              <button
                key={s.key}
                className={`dot ${i === heroIndex ? "active" : ""}`}
                onClick={() => goTo(i)}
                aria-label={`Go to slide ${i + 1}`}
                aria-pressed={i === heroIndex}
              />
            ))}
          </div>
        </div>
      </section>

      {/* ✅ CATEGORY SECTION */}
      <section className="catHero">
        <div className="catHeroHead">
          <h2 className="catHeroTitle">Ayrine’s Style Atlas</h2>
          <p className="catHeroSub">Find Your Perfect Piece</p>
        </div>

        <div className="catGrid">
          {categories.map((c) => (
            <Link key={c.title} to={c.to} className="catCard">
              <div className="catImgWrap">
                <img className="catImg" src={c.img} alt={c.title} />
              </div>
              <div className="catLabel">{c.title}</div>
            </Link>
          ))}
        </div>
      </section>

      {/* FEATURED */}
      <section className="featured">
        <div className="featuredHead">
          <h2 className="featuredTitle">FEATURED</h2>
          <p className="featuredSub">Curated categories designed for timeless everyday elegance.</p>
        </div>

        <div className="featuredGrid">
          {featured.map((item) => (
            <Link key={item.title} to={item.to} className="featuredCard">
              <div className="featuredImgWrap">
                <img className="featuredImg" src={item.img} alt={item.title} />
              </div>
              <div className="featuredLabel">
                <span>{item.title}</span>
              </div>
            </Link>
          ))}
        </div>
      </section>

      {/* ✅ PRICE FILTER */}
      <section className="priceBand" aria-label="Price quick filters">
        <div className="priceBandInner">
          <div className="priceBandKicker">Grace Within Budget</div>

          <div className="priceGrid">
            {priceFilters.map((p) => (
              <Link key={p.price} to={p.to} className="priceCard" aria-label={`${p.label} ${p.price}`}>
                <div className="priceCardTop">{p.label}</div>
                <div className="priceCardAmt">{p.price}</div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* ABOUT */}
      <section className="aboutAyrine">
        <div className="aboutInner">
          <div className="aboutLeft">
            <p className="aboutKicker">About Ayrine</p>
            <h2 className="aboutTitle">Everyday luxury for women — in one place.</h2>
            <p className="aboutText">
              Ayrine brings together elegant artificial jewellery, women’s wear, and premium bags &
              purses — curated for a refined look you can wear on repeat. From festive moments to
              everyday styling, we focus on quality finishes, comfort, and a premium feel that’s
              effortless.
            </p>

            <div className="aboutCtas">
              <Link className="aboutBtnGhost" to="/contact">
                Contact Us
              </Link>
            </div>
          </div>

          <div className="aboutRight" aria-label="Ayrine promise cards">
            {promises.map((p) => (
              <div key={p.title} className="promiseCard">
                <div className="promiseIcon">{p.icon}</div>
                <div className="promiseText">
                  <div className="promiseTitle">{p.title}</div>
                  <div className="promiseSub">{p.sub}</div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ✅ TESTIMONIALS */}
      <section className="testimonials" aria-label="Customer testimonials">
        <div className="testimonialsInner">
          <div className="testiHead">
            <p className="testiKicker">Loved by Customers</p>
            <h2 className="testiTitle">Words that shine brighter than gold</h2>
            <p className="testiSub">
              Average rating <span className="testiAvg">{avgRating}</span> / 5 • Real styling moments, real reviews.
            </p>
          </div>

          <div className="testiGrid">
            {testimonials.map((t) => (
              <div key={t.name + t.place} className="testiCard">
                <div className="testiTop">
                  <div className="testiStars" aria-label={`${t.rating} out of 5 stars`}>
                    {renderStars(t.rating)}
                  </div>
                  <span className="testiTag">{t.tag}</span>
                </div>

                <p className="testiText">“{t.text}”</p>

                <div className="testiFooter">
                  <div className="testiAvatar" aria-hidden="true">
                    {t.name.slice(0, 1).toUpperCase()}
                  </div>
                  <div className="testiPerson">
                    <div className="testiName">{t.name}</div>
                    <div className="testiPlace">{t.place}</div>
                  </div>
                </div>
              </div>
            ))}
          </div>

          <div className="testiCtaRow">
            <Link className="testiCta" to="/contact">
              Share Your Review
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}